import { useMemo, useCallback } from 'react';
import { Col, Row, Card, Button, ButtonGroup, ButtonToolbar } from 'react-bootstrap';
import { useQuery, useMutation, NetworkStatus } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import { validate as uuidValidate } from 'uuid';
import { RefreshCw as RefreshCwIcon, Table as TableIcon } from 'react-feather';

import get from 'lodash.get';

import LinkContainer from '../components/link_container';
import {
  renderOverlay,
  renderOffline,
  renderError,
  renderDateString,
} from '../components/render_helpers';
import ReactTable from '../components/react_table/react_table';
import { sortIsoString } from '../components/react_table/react_table_sorters';
import { toastSuccess, toastWarning, toastError } from '../lib/toast_helpers';
import {
  pilingMachineListPageQuery,
  pilingMachineDelete as pilingMachineDeleteMutation,
} from '../graphql/piling_machine_queries';
import { settingsSet, settingsReset } from '../store/settings_slice';
import * as updateFunctions from '../update_functions';

const PilingMachineList = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.user);
  const settingsMutating = useSelector((state) => state.settings.mutating);
  const settingsOnline = useSelector((state) => state.settings.online);
  const settingsTenant = useSelector((state) => state.settings.tenant);
  const [pilingMachineDelete] = useMutation(pilingMachineDeleteMutation);
  const tableStateKey = 'pilingMachine';

  const {
    data: pageData,
    loading: pageLoading,
    error: pageError,
    refetch: pageRefetch,
    networkStatus: pageNetworkStatus,
    client: pageClient,
  } = useQuery(pilingMachineListPageQuery, {
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ pilingMachineList }) => {
      // write an individual query for every pilingMachine in the list
      updateFunctions.pilingMachineList(pageClient, pilingMachineList);
    },
  });

  const pageLoadedOrRefetching = useMemo(
    () => !pageLoading || (pageLoading && pageNetworkStatus === NetworkStatus.refetch),
    [pageLoading, pageNetworkStatus]
  );

  const tableResetClicked = useCallback(() => {
    dispatch(settingsReset(['tableState', tableStateKey]));
  }, [tableStateKey, dispatch]);

  const pilingMachineDeleteClicked = async (e) => {
    const dataId = e.currentTarget.getAttribute('data-id');
    const pilingMachineId = uuidValidate(dataId)
      ? dataId
      : parseInt(e.currentTarget.getAttribute('data-id'), 10);
    const mutationData = {
      variables: { id: pilingMachineId },
      context: {
        serializationKey: settingsTenant,
        tracked: true,
        recordType: 'PilingMachineType',
        recordId: pilingMachineId,
        mutationType: 'DELETE',
      },
      update: updateFunctions.pilingMachineDelete,
    };
    mutationData.optimisticResponse = updateFunctions.optimistic(
      'pilingMachineDelete',
      mutationData
    );
    dispatch(
      settingsSet({
        mutating: true,
      })
    );
    if (settingsOnline) {
      try {
        await pilingMachineDelete(mutationData);
        toastSuccess('Piling Machine delete ok');
      } catch (err) {
        console.log(err.toString());
        toastError('Piling Machine delete failed');
      } finally {
        dispatch(
          settingsSet({
            mutating: false,
          })
        );
      }
    } else {
      pilingMachineDelete(mutationData);
      toastWarning(`Piling Machine delete ok locally. Go online to make permanent`);
      dispatch(
        settingsSet({
          mutating: false,
        })
      );
    }
  };

  const renderPortalLink = ({ row }) => {
    const naskuMachineRefOriginal = get(row, ['original', 'naskuMachineRef']);
    if (naskuMachineRefOriginal) {
      return (
        <a
          href={`https://service.nasku.net/machine/${naskuMachineRefOriginal}`}
          target="_blank"
          rel="noreferrer"
        >
          Portal
        </a>
      );
    }
    return undefined;
  };

  const parentColumns = [
    {
      header: 'Name',
      accessorKey: 'name',
      enableColumnFilter: true,
      filterFn: 'includesString',
    },
    {
      header: 'Nasku Machine Ref',
      accessorKey: 'naskuMachineRef',
      enableColumnFilter: true,
      filterFn: 'includesString',
    },
    {
      header: 'Nasku Machine Status',
      accessorKey: 'naskuMachineStatus',
      filterType: 'dropdown',
    },
    {
      header: 'Last Online',
      accessorKey: 'naskuLastOnlineAt',
      enableColumnFilter: false,
      accessorFn: ({ naskuLastOnlineAt }) => renderDateString(naskuLastOnlineAt),
      sortingFn: sortIsoString,
    },
    {
      header: 'Portal Link',
      id: 'portal',
      enableColumnFilter: false,
      cell: renderPortalLink,
    },
  ];

  const renderContent = () => (
    <>
      <Row className="mt-4 mb-3">
        <Col>
          <h1 className="h3 mb-3">Piling Machines</h1>
        </Col>
        <Col className="noprint" sm="auto">
          <ButtonToolbar>
            <ButtonGroup className="me-2">
              <LinkContainer to="/piling_machines/new">
                <Button variant="primary">New Piling Machine</Button>
              </LinkContainer>
            </ButtonGroup>
            <ButtonGroup className="me-0">
              <Button title="Reset table filters and sorting" onClick={tableResetClicked}>
                <TableIcon />
              </Button>
              <Button
                title="Refresh data"
                onClick={() => pageRefetch()}
                disabled={!settingsOnline}
              >
                <RefreshCwIcon />
              </Button>
            </ButtonGroup>
          </ButtonToolbar>
        </Col>
      </Row>
      <Row>
        <Col>
          <Card>
            <Card.Body>
              <ReactTable
                rootName={tableStateKey}
                parentColumns={parentColumns}
                data={pageData.pilingMachineList}
                doShow={false}
                doDelete={currentUser?.perms?.administrator}
                handleDelete={pilingMachineDeleteClicked}
                hideResetTable
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );

  return (
    <div>
      {renderOverlay(pageLoading, settingsMutating, settingsOnline)}
      {renderOffline(settingsOnline)}
      {renderError(pageError)}
      {!pageError && pageLoadedOrRefetching && renderContent()}
    </div>
  );
};

export default PilingMachineList;
